import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import toast from "react-hot-toast";

import { BankTransferPanel } from "@/components/features/accountant/BankTransferPanel";
import { CheckoutSummaryPanel } from "@/components/features/payments/checkout-summary-panel";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { serverMessage } from "@/lib/serverMessage";
import { useAuth } from "@/contexts/AuthContext";
import { usePageScroll } from "@/hooks/usePageScroll";
import {
  createBankTransferCheckout,
  createMercadoPagoSubscription,
  createPublicMercadoPagoSubscription,
  getPlanCheckout,
  getPublicCheckout,
} from "@/services/planPaymentsService";
import {
  loadMercadoPagoSdk,
  type MercadoPagoBrickController,
  type MercadoPagoBrickFormData,
} from "@/utils/loadMercadoPagoSdk";

const BRICK_CONTAINER_ID = "mp-payment-brick";

const MERCADOPAGO_PUBLIC_KEY =
  typeof import.meta.env.VITE_MP_PUBLIC_KEY === "string"
    ? import.meta.env.VITE_MP_PUBLIC_KEY
    : "";

type Phase = "loading" | "ready" | "submitting" | "done" | "error";

type Checkout = {
  amountMinor: number;
  currency: string;
  /** Null on the public path: the share-link read does not disclose it. */
  planId: string | null;
  billingInterval: string;
};

/**
 * How the Brick is dressed. Module-level because it never varies per render,
 * and inlining it buried the component's logic under forty lines of style.
 *
 * THE BRICK WEARS OUR BRAND, not Mercado Pago's. These are the literal tokens
 * from index.css, written out because the Brick is configured through a JS
 * object handed to Mercado Pago's iframe: it cannot read a CSS variable from
 * our stylesheet. If the palette moves, this moves with it.
 *
 * COLOUR AND RADIUS ONLY. Gestionalo already paid for the lesson that
 * overriding the Brick's spacing and type sizes breaks it — `formPadding: 0`
 * collapsed its internal gutters and the payment-method radios rendered ON TOP
 * of their own card icons. Its layout is tuned to markup we cannot see.
 *
 * KNOWN LIMITATION: this is the LIGHT palette. The portal has a dark theme, but
 * the Brick is configured once at mount from a static object, so it does not
 * follow a theme switch. Making it track the theme means re-creating the Brick,
 * which would discard whatever the payer has already typed.
 */
function brickCustomization(billingInterval: string) {
  /**
   * INSTALMENTS ARE AN ANNUAL AFFORDANCE ONLY.
   *
   * A monthly plan is a subscription, and a subscription cannot honour
   * instalments at all — `preapproval` has no such field, so a payer who
   * selected 6 cuotas would simply be charged in full every month. Annual is a
   * one-off Order, which is the only one of the two that CAN be split
   * (decision 3ca0532).
   *
   * This is a UI affordance, not a control: the server clamps the count
   * against what the plan permits, because a crafted request ignores whatever
   * the Brick was told.
   */
  const maxInstallments = billingInterval === "annual" ? 12 : 1;
  return {
  paymentMethods: {
    creditCard: "all",
    debitCard: "all",
    // Nothing else is declared: a subscription needs a card it can charge on a
    // schedule, and offering a method that cannot be charged again would
    // strand the payer mid-signup.
    /**
     * NO INSTALLMENTS, because a subscription cannot honour them.
     *
     * Left unset, the Brick shows "Cuotas disponibles" on the credit-card row
     * and offers to split the payment — but `preapproval` has no installments
     * field at all: it charges auto_recurring.transaction_amount once per
     * period, full stop. The badge was therefore advertising a choice the
     * payment could never respect, and a payer who selected 6 cuotas would
     * simply be charged the whole amount every period.
     *
     * Offering real instalments on the annual plan would mean NOT using a
     * subscription for it — a one-off Orders payment instead, which trades
     * away auto-renewal.
     */
    maxInstallments,
    minInstallments: 1,
  },
  visual: {
    style: {
      customVariables: {
        baseColor: "#161514", // --color-primary
        baseColorFirstVariant: "#133024", // --color-primary-hover
        baseColorSecondVariant: "#28241F", // --color-secondary
        textPrimaryColor: "#161514", // --color-foreground
        textSecondaryColor: "#5C554E", // --color-muted-foreground
        inputBackgroundColor: "#ffffff",
        formBackgroundColor: "#ffffff",
        borderRadiusSmall: "8px", // --radius-sm
        borderRadiusMedium: "12px", // --radius-DEFAULT
        borderRadiusLarge: "18px", // --radius-lg
      },
    },
  },
  } as const;
}

function destinationFor(
  returnContext: string,
  outcome: "done" | "timeout",
): string {
  return returnContext === "settings"
    ? `/settings?tab=billing&plan_payment=${outcome}`
    : `/onboarding?plan_payment=${outcome}`;
}

/** How long to wait for the server to grant before handing back a timeout. */
const GRANT_POLL_MS = 2000;
const GRANT_POLL_ATTEMPTS = 5;

/**
 * WAIT FOR THE GRANT INSTEAD OF ASSERTING IT.
 *
 * Reading the checkout is not a status check — `getCheckoutForCompany` runs
 * `activateFromClaim` server-side, so each poll is also an ATTEMPT to grant.
 * That is how a dLocal payer gets activated on their return page, and Mercado
 * Pago simply never had the equivalent: this page navigated straight to
 * `plan_payment=done`, and the settings handler shows "Plan actualizado." off
 * that URL flag alone. A real customer was therefore told their plan had
 * changed while they were still on the old one — the grant had failed and
 * nothing on the page was ever going to notice.
 *
 * Bounded at ~10s: the preapproval is authorized at creation, so the first
 * poll normally succeeds. Polling is not free either — every attempt costs an
 * authenticated read against Mercado Pago, which is the thing that rate-limited
 * us. Returning false is not failure, only "not yet": the money is taken, the
 * claim is recorded, and the 10-minute sweep remains the backstop. The caller
 * says "timeout", never "done".
 */
async function waitForGrant(checkoutId: string): Promise<boolean> {
  for (let attempt = 0; attempt < GRANT_POLL_ATTEMPTS; attempt++) {
    try {
      const row = await getPlanCheckout(checkoutId);
      if (row.status === "paid") return true;
    } catch {
      // A failed read here says nothing about the payment. Keep polling; the
      // sweep covers us if every attempt fails.
    }
    if (attempt < GRANT_POLL_ATTEMPTS - 1) await sleep(GRANT_POLL_MS);
  }
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Mercado Pago checkout — OUR page, with Mercado Pago's Payment Brick in it.
 *
 * There is no redirect to Mercado Pago (decision 633fd8f). The payer enters a
 * card into the Brick here, the Brick returns a single-use token, and the
 * backend creates a subscription that is authorized at creation. The card never
 * touches our DOM — the Brick renders Mercado Pago's own iframes — which keeps
 * us at PCI SAQ A and earns MP's device fingerprint for free.
 *
 * Reached as `/pay/mercadopago?checkout_id=…&return=…`.
 */
export function MercadoPagoPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();

  /**
   * This route renders outside the app shell, which is what normally supplies
   * a scrolling pane — so without this the page cannot be scrolled at all on a
   * viewport shorter than its content, and a payer on a phone cannot reach the
   * Brick's method rows to choose a card.
   */
  usePageScroll();

  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  /**
   * The mounted Brick, so teardown can unmount it. A ref rather than state:
   * re-creating the Brick is exactly what we must not do, and a state update
   * would re-run the effect that creates it.
   */
  const brickRef = useRef<MercadoPagoBrickController | null>(null);

  const returnContext =
    new URLSearchParams(window.location.search).get("return") ?? "onboarding";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    /**
     * TWO WAYS IN, and which one decides whether a session is required.
     *
     * `checkout_id` — the tenant paying for themselves from onboarding or
     * settings, authenticated.
     * `token` — an accountant's client following a shared link, with no
     * session and no prospect of one. The short token IS their authorisation.
     */
    const checkoutId = params.get("checkout_id") ?? "";
    const shareToken = params.get("token") ?? "";

    let cancelled = false;
    const fail = (message: string): void => {
      if (cancelled) return;
      setErrorMessage(message);
      setPhase("error");
    };

    /** Reads the checkout, or null once it has reported why it cannot. */
    async function loadCheckout(): Promise<Checkout | null> {
      if (!checkoutId && !shareToken) {
        fail(t("pay.mercadoPagoMissingCheckout"));
        return null;
      }
      if (!MERCADOPAGO_PUBLIC_KEY) {
        // A deployment problem, not a payer one — say so plainly rather than
        // rendering an empty box where the card form should be.
        fail(t("pay.mercadoPagoUnavailable"));
        return null;
      }
      try {
        if (shareToken) {
          // Public read. Returns only what the Brick needs, so there is no
          // planId — which is also why bank transfer is not offered on this
          // path: it needs a plan and a session.
          const summary = await getPublicCheckout(shareToken);
          return {
            amountMinor: summary.amountMinor,
            currency: summary.currency,
            planId: null,
            billingInterval: summary.billingInterval,
          };
        }
        const row = await getPlanCheckout(checkoutId);
        if (!row || row.status !== "pending") {
          fail(t("pay.mercadoPagoCheckoutNotPending"));
          return null;
        }
        return {
          amountMinor: row.amountMinor,
          currency: row.currency,
          planId: row.planId,
          billingInterval: row.billingInterval,
        };
      } catch {
        fail(t("pay.mercadoPagoCheckoutNotFound"));
        return null;
      }
    }

    /**
     * The Brick calls this with `{ selectedPaymentMethod, formData }` — the
     * INNER formData carries the token. Measured by Gestionalo: forwarding the
     * outer object makes Mercado Pago answer `required_properties`.
     *
     * THROWING IS WHAT RE-ENABLES THE BRICK'S FORM. Returning normally after a
     * decline leaves its button disabled with nothing happening.
     */
    async function submit({
      formData,
    }: {
      formData?: MercadoPagoBrickFormData;
    }): Promise<void> {
      const cardTokenId = formData?.token ?? "";
      const payerEmail = formData?.payer?.email ?? "";
      // Annual needs both: the Orders API must declare credit vs debit, and
      // instalments only exist on that path.
      const paymentMethodId = formData?.payment_method_id;
      const installments = formData?.installments;
      if (!cardTokenId || !payerEmail) {
        setErrorMessage(t("pay.mercadoPagoTokenMissing"));
        setPhase("ready");
        throw new Error("missing_card_token");
      }

      setErrorMessage(null);
      setPhase("submitting");
      try {
        await (shareToken
          ? createPublicMercadoPagoSubscription(shareToken, {
              cardTokenId,
              payerEmail,
              paymentMethodId,
              installments,
            })
          : createMercadoPagoSubscription(checkoutId, {
              cardTokenId,
              payerEmail,
              paymentMethodId,
              installments,
            }));
      } catch (err) {
        /**
         * A FAILED REQUEST DOES NOT PROVE NOTHING WAS CHARGED — a timeout or a
         * 502 can arrive after the subscription was created, and MP debits a
         * card validation immediately.
         *
         * Gestionalo answers this by re-reading the checkout before letting the
         * payer retry. We do not need that client-side race:
         * createMercadoPagoSubscription is idempotent on the ROW, so a second
         * call with a different card returns the preapproval already bound to
         * this checkout instead of creating another. The guarantee lives on the
         * side that knows.
         */
        setErrorMessage(serverMessage(err) ?? t("pay.mercadoPagoFailed"));
        setPhase("ready");
        throw err;
      }
      /**
       * The payer stays on "Confirmando tu suscripción…" while this runs. A
       * share-token payer has no session, so the authenticated poll is not
       * available to them — they go to the thank-you page and the webhook plus
       * the sweep do the granting.
       */
      const granted = shareToken ? true : await waitForGrant(checkoutId);
      setPhase("done");
      window.location.assign(
        shareToken
          ? "/pay/thank-you"
          : destinationFor(returnContext, granted ? "done" : "timeout"),
      );
    }

    async function mount(): Promise<void> {
      const row = await loadCheckout();
      if (cancelled || !row) return;
      setCheckout(row);

      let MercadoPago;
      try {
        MercadoPago = await loadMercadoPagoSdk();
      } catch {
        fail(t("pay.mercadoPagoLoadFailed"));
        return;
      }
      if (cancelled) return;

      const mp = new MercadoPago(MERCADOPAGO_PUBLIC_KEY, { locale: "es-UY" });
      let controller: MercadoPagoBrickController;
      try {
        controller = await mp.bricks().create("payment", BRICK_CONTAINER_ID, {
          initialization: {
            /**
             * MAJOR units, and for DISPLAY ONLY. The Brick shows this and
             * submits it back beside the token; the server ignores what comes
             * back and charges the checkout row. Our rows are minor units.
             */
            amount: row.amountMinor / 100,
          },
          customization: brickCustomization(row.billingInterval),
          callbacks: {
            onReady: () => {
              if (!cancelled) setPhase("ready");
            },
            onSubmit: submit,
            /**
             * SHOW THE ERROR, KEEP THE FORM. The Brick reports recoverable
             * problems here too — a field validation while the payer is still
             * typing — so switching to the `error` phase would hide a Brick
             * that is still mounted and usable, stranding them with a message
             * and no way to act on it.
             */
            onError: () => {
              if (!cancelled) setErrorMessage(t("pay.mercadoPagoFailed"));
            },
          },
        });
      } catch {
        fail(t("pay.mercadoPagoLoadFailed"));
        return;
      }

      /**
       * The page may have unmounted while `create` was in flight. Teardown
       * already ran and saw a null ref, so unmount here or the Brick and its
       * iframes leak — StrictMode's double mount included.
       */
      if (cancelled) {
        controller.unmount();
        return;
      }
      brickRef.current = controller;
    }

    void mount();
    return () => {
      cancelled = true;
      brickRef.current?.unmount();
      brickRef.current = null;
    };
    // Mount once. checkoutId and returnContext come from the URL, and a change
    // to either implies a navigation that remounts this page anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Pay by wire instead. The receipt goes to a human, so this activates on a
   * different clock from the card — annual activates on the spot, a monthly
   * receipt that does not verify is HELD for review.
   *
   * ONLY FOR AN AUTHENTICATED PAYER. The endpoint is `/tenants/current/...`,
   * so an accountant's client following a shared link has no session to post
   * against. Offering a button that cannot work is worse than not offering it.
   */
  async function submitTransfer(
    receipt: File,
    shownCheckoutId: string | null,
  ): Promise<void> {
    // planId is null on the share-link path, where the public read does not
    // disclose it and there is no session to post a receipt against. The
    // panel is already gated on `user`; this makes the dependency explicit
    // rather than leaving it to coincide.
    if (!checkout?.planId) return;
    // The id the panel derived the payer's reference from — settling anything
    // else would make that reference name a row nobody is paying.
    const checkoutId = shownCheckoutId ?? "";
    setTransferSubmitting(true);
    try {
      const result = await createBankTransferCheckout({
        planId: checkout.planId,
        billingInterval: checkout.billingInterval === "annual" ? "annual" : "monthly",
        receipt,
        // SETTLE THIS checkout, do not mint a new one: the reference the payer
        // just wired with is derived from THIS id, and a new row would make it
        // unverifiable — every transfer would read as a mismatch.
        settleCheckoutId: checkoutId,
      });
      /**
       * A HELD transfer is not a completed one. A monthly receipt we looked at
       * and doubted leaves the row paid but the plan NOT granted, so sending
       * the payer to `plan_payment=done` would tell them the opposite of what
       * happened. Only `activated` earns the celebration.
       */
      if (!result.activated) {
        setTransferSubmitting(false);
        setTransferOpen(false);
        setErrorMessage(t("pay.mercadoPagoTransferHeld"));
        return;
      }
      // "done" is honest here without a poll: the branch above already
      // required `result.activated`, so the grant has happened.
      window.location.assign(destinationFor(returnContext, "done"));
    } catch (err) {
      setTransferSubmitting(false);
      toast.error(serverMessage(err) ?? t("pay.mercadoPagoTransferFailed"), {
        duration: 7000,
      });
    }
  }

  return (
    <div className="lg:grid lg:min-h-svh lg:grid-cols-2">
      <CheckoutSummaryPanel
        amountMinor={checkout?.amountMinor ?? null}
        currency={checkout?.currency ?? "UYU"}
        items={
          checkout
            ? [
                {
                  title: t(
                    checkout.billingInterval === "annual"
                      ? "pay.mercadoPagoItemAnnual"
                      : "pay.mercadoPagoItemMonthly",
                  ),
                  amountMinor: checkout.amountMinor,
                },
              ]
            : []
        }
        backHref={
          returnContext === "settings" ? "/settings?tab=billing" : "/onboarding"
        }
      />

      {/* The form half. NO CARD AROUND THE BRICK: it draws its own bordered
          method rows and brings its own padding, so a wrapper would stack two
          insets and box a form that is already boxed. */}
      <main className="px-6 py-8 lg:px-10 lg:py-14">
        <div className="w-full lg:max-w-md">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {t("pay.mercadoPagoTitle")}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("pay.mercadoPagoSubtitle")}
          </p>

          {errorMessage != null && (
            <p role="alert" className="mt-4 text-sm text-danger">
              {errorMessage}
            </p>
          )}

          {(phase === "loading" || phase === "submitting" || phase === "done") && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <LoadingLogo size="lg" />
              <p className="text-sm text-muted-foreground">
                {phase === "loading"
                  ? t("pay.mercadoPagoLoading")
                  : t("pay.mercadoPagoConfirming")}
              </p>
            </div>
          )}

          {/* Always in the DOM once mounting starts: the SDK writes into this
              container by id, and a conditionally rendered node would not
              exist when it goes looking. Hidden, never unmounted.

              VISIBLE ONLY WHEN THE PAYER CAN ACT ON IT. `submitting` and `done`
              used to leave the whole form on screen — card number, holder,
              document and all — stacked under a "Confirmando tu suscripción…"
              spinner, so the page read as though it were still waiting for
              input it had already taken. Hiding it is safe precisely here:
              `submitting` is entered from onSubmit AFTER the Brick has returned
              a card token, so its tokenisation (and any 3DS challenge inside
              it) has already finished. */}
          <div
            id={BRICK_CONTAINER_ID}
            className="mt-1"
            hidden={phase !== "ready"}
          />

          {/**
           * THE OTHER WAY TO PAY, directly under the card button where someone
           * who cannot use a card is already looking — a company account, a
           * limit, or simply a tenant who will not type a card number.
           */}
          {phase === "ready" && checkout?.planId && user ? (
            <div className="mt-4">
              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">
                  {t("pay.mercadoPagoOrPayAnotherWay")}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>

              {/* WHITE, to match the Brick's own card beside it. Scoped to
                  this page with a descendant selector rather than changed on
                  the shared component: in the accountant onboarding and
                  payment-link flows the panel sits on cream ground, where
                  transparent is correct and white would be the odd one out. */}
              <div className="[&_.fx-acct-bank]:mt-0 [&_.fx-acct-bank]:bg-white">
                <BankTransferPanel
                  open={transferOpen}
                  onToggle={() => setTransferOpen((v) => !v)}
                  onSubmit={submitTransfer}
                  submitting={transferSubmitting}
                  amountGrossPesos={checkout.amountMinor / 100}
                  billingInterval={
                    checkout.billingInterval === "annual" ? "annual" : "monthly"
                  }
                  checkoutId={
                    new URLSearchParams(window.location.search).get(
                      "checkout_id",
                    )
                  }
                />
              </div>
            </div>
          ) : null}

          {phase === "ready" && (
            <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
              <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              {t("pay.mercadoPagoCardNote")}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
